import { Inject, Injectable } from '@angular/core';
import { ClientRepository, QuickListDataSourceFactory } from '@skylab-angular-shared/business';
import { L10n } from '@skylab-angular-shared/core';
import { DialogInsService, DialogResolution } from '@skylab-angular-shared/ui';
import { ClientQLCollectionItem, ClientQLCollectionQuery, ClientRepositoryBase, ContextKeys, DocumentAdjustedBuyerNotSameTinError, DocumentAdjustedClientEmptyBuyerOnAdjusted, DocumentAdjustedClientEmptyBuyerOnAdjustment, DocumentAdjustedClientNotSameTinError, QuickListDataSourceFactoryBase, QuickListFilterMethod, fillDocumentClientBasedOnClient, getBuyerSummary, quickListFilterMethod } from '@skylab-shared/business';
import { BusinessObjectBase, ConsoleLogger, L10nBase, LocalContext, ModelBase, RepositoryBase, Violation } from '@skylab-shared/core';
import { AddressModel, ClientModel, DocumentBuyerModel, DocumentClientModel } from '@skylab-shared/dataModel';
import { isString, isUndefined, upperFirst } from '@skylab-shared/utils';
import { ClientDialogComponent, ClientDialogContext, ClientFormDialogData, ClientFormDialogResult } from '../dialogs/client/client-dialog/client-dialog.component';
import { FormBase, FormBaseViewModel } from '../form/form-base';

type SetByIdValue = {
    id: number;
    name: string;
}
type SetByModelValue = { clientModel: ClientModel; buyerModel?: ClientModel };
type SetClientValue = SetByIdValue | SetByModelValue;
type ExtensonOptions<TProp> = {
    clientProperty: TProp,
    buyerProperty?: TProp,

    previousClientModel?: DocumentClientModel | null;
    previousBuyerModel?: DocumentBuyerModel | null;

    context: ClientDialogContext,
    previewMode?: () => boolean
};

type ClientPropertyName<T extends string> = T;
type ClientFilterPropertyName<T extends string> = `${T}Filter`;
type HandleNameChangePropertyName<T extends string> = `handle${Capitalize<T>}NameChange`;
type OnFavouriteSwitchPropertyName<T extends string> = `on${Capitalize<T>}FavouriteSwitch`;
type AddDialogPropertyName<T extends string> = `showAdd${Capitalize<T>}Dialog`;
type GetBuyerSummary<T extends string> = `get${Capitalize<T>}Summary`;
type EditDialogPropertyName<T extends string> = `showEdit${Capitalize<T>}Dialog`;
type PreviewDialogPropertyName<T extends string> = `preview${Capitalize<T>}Dialog`;
type ClientReadonlyPropertyName<T extends string> = `${T}Readonly`;
type ClientChangedInfoPropertyName<T extends string> = `${T}ChangedInfo`;

export type DocumentFormViewModelClientExtension<T extends string, TBuyer extends string> =
    & { readonly [x in ClientPropertyName<T>]: { value: SetByIdValue }; }
    & { readonly [x in ClientFilterPropertyName<T>]: QuickListFilterMethod<ClientQLCollectionItem>; }
    & { [x in HandleNameChangePropertyName<T>]: (name: string) => void; }
    & { [x in OnFavouriteSwitchPropertyName<T>]: () => void; }
    & { [x in AddDialogPropertyName<T>]: () => Promise<void>; }
    & { [x in EditDialogPropertyName<T>]: () => Promise<void>; }
    & { [x in PreviewDialogPropertyName<T>]: () => Promise<void>; }
    & { [x in ClientReadonlyPropertyName<T>]: () => boolean; }
    & { [x in ClientChangedInfoPropertyName<T>]: () => string; }
    & { [x in GetBuyerSummary<TBuyer>]: () => string; };

@Injectable({ providedIn: 'root' })
export class DocumentFormViewModelClientExtensionService {
    public constructor(
        private readonly dialogService: DialogInsService,
        @Inject(L10n) private readonly l10n: L10nBase,
        @Inject(QuickListDataSourceFactory) private readonly quickListDataSourceFactory: QuickListDataSourceFactoryBase,
        @Inject(ClientRepository) private readonly clientRepository: ClientRepositoryBase) { }

    public extendViewModel<TViewModelPart extends FormBaseViewModel, TViewModel extends TViewModelPart, TBO extends BusinessObjectBase<TModel>, TModel extends ModelBase & { [P in TBoClientProperty]: DocumentClientModel | null }, TBoClientProperty extends (keyof TModel & string)>(
        options: ExtensonOptions<TBoClientProperty>,
        viewModel: TViewModelPart,
        formComponent: FormBase<TViewModel , RepositoryBase<TBO, TModel>, TBO, TModel>,
        localContext: LocalContext,
        onClientChanged?: (client?: ClientModel | null) => Promise<void>): TViewModelPart & DocumentFormViewModelClientExtension<TBoClientProperty, TBoClientProperty> {
        return Object.assign(viewModel, this.getViewModelExtension(options, formComponent, localContext, onClientChanged));
    }

    private getViewModelExtension<TViewModel extends FormBaseViewModel, TBO extends BusinessObjectBase<TModel>, TModel extends ModelBase & { [P in TBoClientProperty]: DocumentClientModel | null }, TBoClientProperty extends (keyof TModel & string)>(
        options: ExtensonOptions<TBoClientProperty>,
        formComponent: FormBase<TViewModel, RepositoryBase<TBO, TModel>, TBO, TModel>,
        localContext: LocalContext,
        onClientChanged?: (client?: ClientModel | null) => Promise<void>): DocumentFormViewModelClientExtension<TBoClientProperty, TBoClientProperty> {
        const logger = new ConsoleLogger(this.constructor.name);
        const clientDataSource = this.quickListDataSourceFactory.createCacheable(ClientQLCollectionQuery);
        const clientRepository = this.clientRepository;
        const model = formComponent.bo.model;
        const { clientProperty, buyerProperty, context } = options;
        const initialClient = model.get<DocumentClientModel>(clientProperty);
        const propertyNamePart = <Capitalize<TBoClientProperty>>upperFirst(clientProperty);
        const buyerPropertyNamePart = buyerProperty ? <Capitalize<TBoClientProperty>>upperFirst(buyerProperty) : null;

        formComponent.registerListening(() => clientDataSource.dispose());

        let client: SetByIdValue | null | any = initialClient
            ? { id: initialClient.sourceClientId, name: initialClient.name, }
            : null;
        let handledClientName: string | null;
        let changedInfo: string | null;

        const refreshChangedInfo = () => {
            if (!options.previousClientModel) return;
            const clientModel = model.get(clientProperty) as DocumentClientModel;
            const buyerModel = buyerProperty ? model.get(buyerProperty) as DocumentBuyerModel : null;
            changedInfo = this.getChangesInfo(clientModel, options.previousClientModel, buyerModel, options.previousBuyerModel);
        }

        refreshChangedInfo();

        const viewModelExtension = {
            [clientProperty]: {
                get value(): SetByIdValue {
                    return client;
                },
                set value(value: SetClientValue | null) {
                    if (value === null) {
                        client = null;
                        model.set(clientProperty, null);
                        onClientChanged?.(null);
                    } else if (value instanceof Object && 'clientModel' in value) {
                        client = value ? { id: value.clientModel.id, name: value.clientModel.name } : null;
                        (async () => {
                            const documentClient = new DocumentClientModel();
                            model.set(clientProperty, documentClient);
                            fillDocumentClientBasedOnClient(documentClient, value.clientModel);
                            if (value.buyerModel) {
                                const documentBuyer = new DocumentBuyerModel();
                                if (buyerProperty) model.set(buyerProperty, documentBuyer);
                                fillDocumentClientBasedOnClient(documentBuyer, value.buyerModel);
                                if (!isUndefined(value.clientModel.buyerRole)) documentBuyer.role = value.clientModel.buyerRole;
                            } else {
                                if (buyerProperty) model.set(buyerProperty, null);
                            }
                            await onClientChanged?.(value.clientModel);
                        })().catch(err => logger.error(err));
                    } else if (value.id){
                        client = { id: value.id, name: value.name };
                        (async () => {
                            const client = await localContext.getAsync(ContextKeys.client(value.id), () => clientRepository.findAsync(value.id));
                            const documentClient = new DocumentClientModel();
                            model.set(clientProperty, documentClient);
                            fillDocumentClientBasedOnClient(documentClient, client);
                            if (client.buyerId) {
                                const documentBuyer = new DocumentBuyerModel();
                                if (buyerProperty) model.set(buyerProperty, documentBuyer);
                                const buyer = await localContext.getAsync(ContextKeys.client(client.buyerId), () => clientRepository.findAsync(client.buyerId!));
                                fillDocumentClientBasedOnClient(documentBuyer, buyer);
                                if (!isUndefined(client.buyerRole)) documentBuyer.role = client.buyerRole;
                            } else {
                                if (buyerProperty) model.set(buyerProperty, null);
                            }
                            await onClientChanged?.(client);
                        })().catch(err => logger.error(err));
                    }
                },
            },
            [`${clientProperty}Filter`]: <QuickListFilterMethod<ClientQLCollectionItem>>quickListFilterMethod(clientDataSource),
            [`handle${propertyNamePart}NameChange`]: (name: string): void => {
                handledClientName = name || null;
            },
            [`on${propertyNamePart}FavouriteSwitch`]: () => {
                clientDataSource.clearCache();
            },
            [`showAdd${propertyNamePart}Dialog`]: async (): Promise<void> => {
                const name = handledClientName ?? '';
                const result = await this.showClientDialogAsync({ localContext, clientNameOrTin: name, context });
                if (result.data) {
                    viewModelExtension[clientProperty].value = { clientModel: result.data.clientModel, buyerModel: result.data.buyerModel } as any;
                    clientDataSource.clearCache();
                }
            },
            [`showEdit${propertyNamePart}Dialog`]: async (): Promise<void> => {
                const clientModel = model.get(clientProperty) as DocumentClientModel;
                const buyerModel = buyerProperty ? model.get(buyerProperty) as DocumentBuyerModel : null;

                const result = await this.showClientDialogAsync({
                    localContext,
                    clientModel,
                    buyerModel,
                    context,
                    previousBuyerModel: options.previousBuyerModel,
                    previousClientModel: options.previousClientModel,
                    previewMode: options.previewMode?.() || false,
                    goToClientsMessages: {
                        client: (!!clientModel.sourceClientId && this.clientRepairMessage(formComponent.bo)) || '',
                        buyer: (!!buyerModel?.sourceClientId && this.buyerRepairMessage(formComponent.bo)) || '',
                    }
                });
                if (result.data) {
                    viewModelExtension[clientProperty].value = { clientModel: result.data.clientModel, buyerModel: result.data.buyerModel } as any;
                    clientDataSource.clearCache();
                    refreshChangedInfo();
                }
            },
            [`${clientProperty}Readonly`]: () => {
                return formComponent.bo.readonlyProperties.some(p => p.propertyName === clientProperty && p.readonly);
            },
            [`${clientProperty}ChangedInfo`]: () => changedInfo,
            [`get${buyerPropertyNamePart}Summary`]: () => {
                const buyerModel = buyerProperty ? model.get(buyerProperty) as DocumentBuyerModel : null;
                return buyerModel ? getBuyerSummary(buyerModel) : '';
            }
        } as DocumentFormViewModelClientExtension<TBoClientProperty, TBoClientProperty>;

        return viewModelExtension;
    }

    private showClientDialogAsync(data: ClientFormDialogData): Promise<DialogResolution<ClientFormDialogResult>> {
        return this.dialogService.showAsync<{ data: ClientFormDialogData }, ClientFormDialogResult>({
            component: ClientDialogComponent,
            componentProps: { data },
            cssClass: data.previousClientModel ? 'modal--wide' : 'modal--regular',
        });
    }

    private getChangesInfo(clientModel: DocumentClientModel, previousClientModel: DocumentClientModel, buyerModel?: DocumentBuyerModel | null,  previousBuyerModel?: DocumentBuyerModel | null): string {
        const clientChanged = this.diffClient(clientModel, previousClientModel);
        const buyerChanged = this.diffBuyer(buyerModel, previousBuyerModel);
        return (clientChanged || buyerChanged) ? this.l10n._T('scenes.shared.document-form.extensions.client.changes-info') : '';
    }

    private diffClient(a: DocumentClientModel | null | undefined, b: DocumentClientModel | null | undefined, compareEmails = true): boolean {
        if (!a && !b) return false;

        const clientFields: (keyof DocumentClientModel)[] = ['name', 'tin', 'tinKind', 'kind', 'internalIdentificationNumber'];
        if (this.hasDifferentFields(a, b, clientFields)) return true;

        if (compareEmails && !this.arraysEqual(a?.emails, b?.emails)) return true;
        const addressFields: (keyof AddressModel)[] = ['name', 'city', 'countryId', 'line1', 'line2', 'zipCode'];

        return this.hasDifferentFields(a?.address, b?.address, addressFields);
    }

    private diffBuyer(a: DocumentBuyerModel | null | undefined, b: DocumentBuyerModel | null | undefined): boolean {
        if (!a && !b) return false;
        if ((a && !b) || (!a && b)) return true;

        return this.diffClient(a, b, false) || a?.role !== b?.role;
    }

    private hasDifferentFields<T>(a: T | null | undefined, b: T | null | undefined, fields: (keyof T)[]): boolean {
        const norm = (v: unknown) => (isString(v) ? v.trim() : v) ?? '';
        return fields.some(field => norm(a?.[field]) !== norm(b?.[field]));
    }

    private arraysEqual<T>(a: T[] | null | undefined, b: T[] | null | undefined): boolean {
        const _a = a || [];
        const _b = b || [];
        return _a.length === _b.length && _a.every((v, i) => v === _b[i]);
    }

    private isErrorOnModel(bo: BusinessObjectBase<any>, violation: (new (...args: any[]) => Violation)): boolean {
        return bo.validationResult.some(e => e.ruleViolation.violation instanceof violation);
    }

    private clientRepairMessage(bo: BusinessObjectBase<any>): string | null {
        if (this.isErrorOnModel(bo, DocumentAdjustedClientNotSameTinError)) return this.l10n._T('business.document.common.validation.document-adjustment.client-not-same-tin.message');
        if (this.isErrorOnModel(bo, DocumentAdjustedClientEmptyBuyerOnAdjustment)) return this.l10n._T('business.document.common.validation.document-adjustment.client-empty-buyer-on-adjustment.message');
        if (this.isErrorOnModel(bo, DocumentAdjustedClientEmptyBuyerOnAdjusted)) return this.l10n._T('business.document.common.validation.document-adjustment.client-empty-buyer-on-adjusted.message');
        return null;
    }

    private buyerRepairMessage(bo: BusinessObjectBase<any>): string | null {
        if (this.isErrorOnModel(bo, DocumentAdjustedBuyerNotSameTinError)) return this.l10n._T('business.document.common.validation.document-adjustment.buyer-not-same-tin.message');
        return null;
    }
}
